import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import {
  useAssetProperties,
  isPendingApprovalValue,
  refreshAssetProperties,
  refreshIssues,
  type AssetPropertyValue,
} from '../../hooks/useQueries'
import type { SourceRef } from './primitives'

/** Spec §11.1 — until calibrated, treat the auto-merge confidence threshold as 0.85. */
export const CONF_THRESHOLD = 0.85

export type ReliabilityCandidate = {
  uid: string
  value: string
  source?: SourceRef
  /** 0..1 confidence; undefined => treat as high-confidence structured extraction */
  confidence?: number
}

export type ReliabilityChange = {
  propertyUid: string
  field: string
  current: { value: string; source?: SourceRef } | null
  candidates: ReliabilityCandidate[]
}

export type ReliabilityValidated = {
  propertyUid: string
  field: string
  value: string
  source?: SourceRef
  /**
   * True when classify() (backend) merged this value without a human ever
   * clicking accept — i.e. it came from document extraction and carries no
   * approver. Drives the "Validé automatiquement" collapsed block (spec
   * §7.1, acceptance criterion #2): AUTO items render there, distinct from
   * ordinary confirmed values.
   */
  isAutoMerged: boolean
}

function stringifyValue(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

function sourceFromValue(p: AssetPropertyValue): SourceRef | undefined {
  const sd = (p as { source_details?: Record<string, unknown> }).source_details
  if (!sd) {
    const code = (p as { source_code?: string }).source_code
    return code === 'manual' ? { manualLabel: 'Saisie' } : undefined
  }
  const documentUid = sd.document_uid as string | undefined
  const documentName = sd.document_filename as string | undefined
  const page = sd.page as number | string | undefined
  const snippet = sd.snippet as string | undefined
  if (documentUid || documentName) {
    return { documentUid, documentName, page, snippet }
  }
  if (sd.source === 'manual') return { manualLabel: 'Saisie' }
  return undefined
}

function confidenceOf(p: AssetPropertyValue): number | undefined {
  const sd = (p as { source_details?: { confidence?: number } }).source_details
  return sd?.confidence
}

function propertyName(p: AssetPropertyValue, locale: string): string {
  const def = (p as { property_definition?: { name?: Record<string, string>; code?: string } }).property_definition
  const propUid = (p as { property_uid?: string }).property_uid
  if (!def?.name) return def?.code || propUid || ''
  return def.name[locale] || def.name.en || def.name.fr || def.code || propUid || ''
}

/**
 * Reconciliation view-model for a building's scalar properties.
 *
 * Registry  = confirmed asset_data rows (no `to_approve` flag).
 * Candidates = `to_approve` asset_data rows, grouped by property definition.
 * A property with ≥1 candidate becomes a `ChangeCard`; everything else (or a
 * confirmed value with no pending candidate) is folded into "validated".
 */
export function useImmeubleReliability(org: string, assetUid: string, locale: string) {
  const queryClient = useQueryClient()
  const { data: all = [], isLoading, error } = useAssetProperties(org, assetUid)
  const [processing, setProcessing] = useState(false)

  const { changes, validated } = useMemo(() => {
    const confirmed = all.filter((p) => !isPendingApprovalValue(p))
    const pending = all.filter((p) => isPendingApprovalValue(p))

    const confirmedByProp = new Map<string, AssetPropertyValue>()
    for (const c of confirmed) {
      const key = (c as { property_uid?: string }).property_uid || ''
      if (key) confirmedByProp.set(key, c)
    }

    // group pending candidates by property definition
    const pendingByProp = new Map<string, AssetPropertyValue[]>()
    for (const p of pending) {
      const key = (p as { property_uid?: string }).property_uid || ''
      if (!key) continue
      const arr = pendingByProp.get(key) || []
      arr.push(p)
      pendingByProp.set(key, arr)
    }

    const changes: ReliabilityChange[] = []
    for (const [propertyUid, cands] of pendingByProp) {
      const cur = confirmedByProp.get(propertyUid)
      changes.push({
        propertyUid,
        field: propertyName(cands[0], locale),
        current: cur
          ? { value: stringifyValue((cur as { value?: unknown }).value), source: sourceFromValue(cur) }
          : null,
        candidates: cands.map((c) => ({
          uid: (c as { uid: string }).uid,
          value: stringifyValue((c as { value?: unknown }).value),
          source: sourceFromValue(c),
          confidence: confidenceOf(c),
        })),
      })
    }

    // validated = confirmed values that have no pending candidate competing
    const validated: ReliabilityValidated[] = confirmed
      .filter((c) => {
        const key = (c as { property_uid?: string }).property_uid || ''
        return key && !pendingByProp.has(key)
      })
      .map((c) => {
        const sourceCode = (c as { source_code?: string }).source_code
        const approvedByUid = (c as { approved_by_uid?: string | null }).approved_by_uid
        return {
          propertyUid: (c as { property_uid?: string }).property_uid || '',
          field: propertyName(c, locale),
          value: stringifyValue((c as { value?: unknown }).value),
          source: sourceFromValue(c),
          isAutoMerged: (sourceCode === 'document_extraction' || sourceCode === 'ai_extraction') && !approvedByUid,
        }
      })

    return { changes, validated }
  }, [all, locale])

  const post = useCallback(
    async (body: { approve?: string[]; reject?: string[] }) => {
      setProcessing(true)
      try {
        await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve', {
          params: { path: { orgCode: org, assetUid } },
          body: { approve: body.approve || [], reject: body.reject || [] },
        })
        refreshAssetProperties(queryClient, org, assetUid)
        refreshIssues(queryClient, org)
      } finally {
        setProcessing(false)
      }
    },
    [org, assetUid, queryClient],
  )

  /** Accept one candidate; reject any sibling candidates on the same property. */
  const acceptCandidate = useCallback(
    (change: ReliabilityChange, candidateUid: string) =>
      post({
        approve: [candidateUid],
        reject: change.candidates.filter((c) => c.uid !== candidateUid).map((c) => c.uid),
      }),
    [post],
  )

  /** Keep the current registry value; reject every competing candidate. */
  const keepCurrent = useCallback(
    (change: ReliabilityChange) => post({ reject: change.candidates.map((c) => c.uid) }),
    [post],
  )

  return { changes, validated, isLoading, error, processing, acceptCandidate, keepCurrent }
}
